import { useState } from "react";
import { useNow } from "../../hooks/useNow";
import { WeatherTile } from "../WeatherTile";
import { AreaTimetableSheet } from "../AreaTimetable";
import { useFirestoreCollection } from "../../hooks/useFirestoreCollection";
import { useCounterPulse } from "../../hooks/useFreshHighlight";
import {
  programmeringByMin,
  VENUE_LIST,
  ecc,
  showDayInzet,
} from "../../lib/data";
import { isCurrent, minutesNow, formatRel } from "../../lib/time";
import type {
  ChecklistEntry,
  IncidentEntry,
  ProgrammeItem,
} from "../../lib/types";

interface ProgItem extends ProgrammeItem {
  vanMin: number | null;
  totMin: number | null;
}

export function KpiStrip() {
  const [openVenue, setOpenVenue] = useState<string | null>(null);
  const now = useNow(15_000);
  const nowMin = minutesNow(now);

  const { data: incidents } = useFirestoreCollection<IncidentEntry>(
    "incidents",
    { orderField: "time", orderDir: "desc" },
  );
  const { data: checklist } = useFirestoreCollection<ChecklistEntry>(
    "checklist",
  );
  const checkedSet = new Set(
    checklist.filter((c) => c.checked).map((c) => c.id),
  );

  const acts = programmeringByMin.filter((p) => !p.is_overleg) as ProgItem[];
  const actsTotal = acts.length;
  const actsElapsed = acts.filter(
    (a) => a.totMin != null && a.totMin <= nowMin,
  ).length;
  const actsLive = acts.filter((a) =>
    isCurrent(a.vanMin, a.totMin, nowMin),
  ).length;

  const eccTotal = ecc.length;
  const eccChecked = ecc.filter((e) => checkedSet.has(e.id)).length;
  const nextEcc = ecc.find((e) => e.vanMin != null && e.vanMin > nowMin);

  const positiesTotal = showDayInzet.length;
  const positiesOnStation = showDayInzet.filter((i) =>
    checkedSet.has(`pos:${i.id}`),
  ).length;

  const openIncidents = incidents.filter((i) => !i.resolved);
  const openAlarm = openIncidents.filter(
    (i) => i.severity === "alarm" || i.severity === "actie",
  ).length;

  return (
    <div className="space-y-3">
      {/* Top row — counter KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <WeatherTile />
        <Kpi
          label="Programma"
          value={`${actsElapsed}/${actsTotal}`}
          progress={actsElapsed / actsTotal}
          subtitle={
            actsLive > 0
              ? `${actsLive} live nu`
              : `${actsTotal - actsElapsed} te gaan`
          }
          accent={actsLive > 0 ? "ok" : "default"}
        />
        <Kpi
          label="ECC"
          value={`${eccChecked}/${eccTotal}`}
          progress={eccChecked / eccTotal}
          subtitle={
            nextEcc
              ? `Volgend ${nextEcc.tijd_van} · ${formatRel(nextEcc.vanMin!, nowMin)}`
              : "Geen ECC meer"
          }
          accent={
            nextEcc && nextEcc.vanMin != null && nextEcc.vanMin - nowMin <= 15
              ? "warn"
              : "default"
          }
        />
        <Kpi
          label="Posities"
          value={`${positiesOnStation}/${positiesTotal}`}
          progress={positiesOnStation / positiesTotal}
          subtitle={
            positiesOnStation === 0
              ? "Nog niemand op positie"
              : `${positiesTotal - positiesOnStation} nog te bemannen`
          }
          accent={
            positiesOnStation === positiesTotal
              ? "ok"
              : positiesOnStation > 0
                ? "warn"
                : "default"
          }
        />
        <Kpi
          label="Open log"
          value={`${openIncidents.length}`}
          subtitle={
            openAlarm > 0
              ? `${openAlarm} actie/alarm`
              : openIncidents.length > 0
                ? "info/let-op"
                : "Alles onder controle"
          }
          accent={openAlarm > 0 ? "risk" : openIncidents.length > 0 ? "warn" : "ok"}
        />
      </div>

      {/* Bottom row — per-podium live status */}
      <div className="grid grid-cols-3 gap-3">
        {VENUE_LIST.map((venue) => {
          const venueActs = acts.filter((p) => p.locatie.venue === venue);
          const current = venueActs.find((p) =>
            isCurrent(p.vanMin, p.totMin, nowMin),
          );
          const next = venueActs.find(
            (p) => p.vanMin != null && p.vanMin > nowMin,
          );
          const elapsedMin =
            current && current.vanMin != null
              ? Math.max(0, nowMin - current.vanMin)
              : 0;
          const totalMin =
            current && current.vanMin != null && current.totMin != null
              ? current.totMin - current.vanMin
              : 0;
          const progress =
            totalMin > 0 ? Math.min(1, elapsedMin / totalMin) : 0;

          return (
            <PodiumTile
              key={venue}
              onOpen={() => setOpenVenue(venue)}
              venue={venue}
              current={current ?? null}
              next={next ?? null}
              elapsedMin={elapsedMin}
              totalMin={totalMin}
              progress={progress}
            />
          );
        })}
      </div>
      {openVenue && (
        <AreaTimetableSheet
          venue={openVenue}
          nowMin={nowMin}
          onClose={() => setOpenVenue(null)}
        />
      )}
    </div>
  );
}

function Kpi({
  label,
  value,
  subtitle,
  progress,
  accent = "default",
}: {
  label: string;
  value: string;
  subtitle?: string;
  progress?: number;
  accent?: "default" | "ok" | "warn" | "risk";
}) {
  // Pulse the value when it changes (numeric tween + scale).
  // For "12/35" style values, take the lhs as the changing metric.
  const numericPart = parseInt(String(value).split("/")[0], 10);
  const valueRef = useCounterPulse<HTMLSpanElement>(
    Number.isFinite(numericPart) ? numericPart : value,
  );
  const valueColor =
    accent === "risk"
      ? "text-risk"
      : accent === "warn"
        ? "text-warn"
        : accent === "ok"
          ? "text-ok"
          : "text-zinc-100";
  const dot =
    accent === "risk"
      ? "bg-risk"
      : accent === "warn"
        ? "bg-warn"
        : accent === "ok"
          ? "bg-ok"
          : "bg-zinc-600";

  // Split "12/35" so we can pulse just the lhs
  const [lhs, rhs] = String(value).split("/");
  return (
    <div className="card p-3.5">
      <div className="flex items-center gap-2 mb-2">
        <span className={`w-1 h-1 rounded-full ${dot}`} />
        <span className="eyebrow">{label}</span>
      </div>
      <div
        className={`text-3xl tabular font-semibold leading-none tracking-tight ${valueColor}`}
        style={{ fontFeatureSettings: '"ss01", "cv11"' }}
      >
        <span ref={valueRef} className="inline-block origin-left">
          {lhs}
        </span>
        {rhs != null && <span className="text-zinc-600">/{rhs}</span>}
      </div>
      {progress != null && (
        <div className="h-0.5 bg-white/[0.05] rounded-full overflow-hidden mt-3">
          <div
            className={`h-full transition-all duration-500 ease-spring ${dot}`}
            style={{ width: `${Math.round(progress * 100)}%` }}
          />
        </div>
      )}
      {subtitle && (
        <div className="text-2xs text-zinc-500 mt-2 truncate tabular">
          {subtitle}
        </div>
      )}
    </div>
  );
}

function PodiumTile({
  venue,
  current,
  next,
  elapsedMin,
  totalMin,
  progress,
  onOpen,
}: {
  venue: string;
  onOpen: () => void;
  current: ProgItem | null;
  next: ProgItem | null;
  elapsedMin: number;
  totalMin: number;
  progress: number;
}) {
  return (
    <div role="button" onClick={onOpen} className="cursor-pointer card p-3.5">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span
            className={`w-1 h-1 rounded-full ${current ? "bg-ok" : "bg-zinc-600"}`}
          />
          <span className="eyebrow">{venue}</span>
        </div>
        {current && (
          <span className="pill bg-ok/15 text-ok">
            <span className="w-1.5 h-1.5 rounded-full bg-ok animate-pulseSoft" />
            Live
          </span>
        )}
      </div>
      {current ? (
        <>
          <div className="font-semibold text-zinc-100 truncate text-base">
            {current.has_aandacht && (
              <span className="text-warn mr-1.5">⚠</span>
            )}
            {current.wat}
          </div>
          <div className="h-0.5 bg-white/[0.05] rounded-full overflow-hidden mt-2.5">
            <div
              className="h-full bg-ok transition-all duration-1000 ease-spring"
              style={{ width: `${Math.round(progress * 100)}%` }}
            />
          </div>
          <div className="text-2xs text-zinc-500 tabular mt-2 flex items-center justify-between">
            <span>
              {elapsedMin}/{totalMin} min
            </span>
            {next && (
              <span className="text-zinc-600 truncate ml-2">
                → {next.tijd_van} {next.wat}
              </span>
            )}
          </div>
        </>
      ) : next ? (
        <>
          <div className="text-zinc-500 text-sm">— stil —</div>
          <div className="h-0.5 bg-white/[0.05] rounded-full mt-2.5" />
          <div className="text-2xs text-zinc-600 mt-2 truncate tabular">
            Volgend: {next.tijd_van} · {next.wat}
          </div>
        </>
      ) : (
        <>
          <div className="text-zinc-600 text-sm">— afgelopen —</div>
          <div className="h-0.5 bg-white/[0.05] rounded-full mt-2.5" />
          <div className="text-2xs text-zinc-700 mt-2">
            Programma op dit podium afgesloten
          </div>
        </>
      )}
    </div>
  );
}
